import {
  ElementKind,
  Project,
  IBuilding,
  ProjectMetadata,
  FootprintMetadata,
  Baseline,
  defaultBuildingFeatures,
} from '../models/project.model';
import { omit, omitUndefined } from './object.helpers';
import { createElementOfType } from './element-factory.helpers';
import { cloneDeep } from 'lodash';
import { getTimestamps } from './date.helpers';
import {
  IFactoryBuilding,
  IFactoryProject,
  IFactoryVersion,
} from '../models/factory-element.model';
import { Coordinate } from '@shared/models/geography.model';
import { createMarketEstimatesSnapshot } from './product-market-estimates.helpers';
import { ActivityId } from '@shared/models/activity-class.model';
import { Activity } from '@shared/models/activities.model';
import { applyMissingCalculatedAreaActivities } from './design/calculated-area-activities.helpers';
import { migrateProjectMetadataToBuildingDesign } from './design/migration/design-migrations.helpers';
import { getDesignGfa } from './design/shape.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { ensureDesignHasBuilding } from './design/factories/design.factory';
import { ensureVersionHasProposal } from './proposal-factory.helpers';

type IFactoryProjectWithVersions = IFactoryProject & {
  /**
   * Note versions belong to buildings, not projects.
   * This is a shortcut to add versions to the first building.
   */
  versions?: Partial<IFactoryVersion>[];
};

type ProjectMetadataFactoryExtensions = {
  building_lifetime?: number;
  below_ground?: number;
  storeys?: ProjectMetadataStorey[];
  gfa_building?: number;
  building_perimeter?: number;
  activity_id?: ActivityId;
  activities?: Activity[];
};

type ProjectMetadataStorey = {
  name?: string;
  gfa?: number;
  facade_edge?: number;
  mezzanine_edge?: number;
  inner_height?: number;
};

export const initialCoordinates: Readonly<Coordinate[]> = [
  {
    lat: 59.341518,
    lng: 18.063094,
  },
  {
    lat: 59.341796,
    lng: 18.06412,
  },
  {
    lat: 59.341409,
    lng: 18.064489,
  },
  {
    lat: 59.341205,
    lng: 18.063405,
  },
];

export const BUILDING_LIFETIME_DEFAULT = 50;

/**
 * Default building metadata. `building_footprint` is legacy geometry kept only to seed the
 * default {@link BuildingDesign} (and tests); production metadata is design-only.
 */
export const defaultMetadata: Readonly<
  ProjectMetadata & { building_footprint: FootprintMetadata }
> = {
  building_footprint: {
    // area and perimeter should be calculated by the coordinates instead in the future
    area: 2851.046267153661, // The default area you get when calculating the footprint from the initial coordinates
    perimeter: 218.50331550561117, // The default perimeter you get when calculating the footprint from the initial coordinates
    coordinates: [...initialCoordinates],
  },
  design: { gfa: 2851.046267153661 },
};

/**
 * Create a building metadata with defaults. Will simplyfy to apply area and perimeter by allowing to pass them as defaults (in root).
 * @param defaults Defaults to override.
 * @returns Building metadata.
 */
export const createProjectMetadata = <
  T extends Record<string, unknown> = ProjectMetadataFactoryExtensions,
>(
  defaults: Partial<ProjectMetadata & { area: number; perimeter: number }> &
    T = {} as Partial<ProjectMetadata & { area: number; perimeter: number }> &
    T,
): ProjectMetadata & T => {
  const meta = cloneDeep({
    ...defaultMetadata,
    ...omit(defaults, 'area', 'perimeter'), // area and perimeter are not part of the metadata, but we allow to pass them as defaults to simplify the creation of metadata.
  });

  if (defaults.area) {
    meta.building_footprint.area = defaults.area;
    (meta as ProjectMetadata).design = { gfa: defaults.area };
  }
  if (defaults.perimeter) {
    meta.building_footprint.perimeter = defaults.perimeter;
  }

  if (!meta.design) {
    (meta as ProjectMetadata).design = {
      gfa: Math.max(1, meta.building_footprint.area),
    };
  }

  return meta as ProjectMetadata & T;
};

/**
 * Resolve project-level {@link BuildingDesign} from an explicit design or legacy
 * {@link ProjectMetadata}, and sync the metadata GFA snapshot (DEV-2576).
 * @param meta Project metadata.
 * @param design Optional explicit design.
 * @returns Aligned meta + design pair.
 */
const resolveDesignAndMeta = (
  meta: ProjectMetadata,
  design?: BuildingDesign,
): { meta: ProjectMetadata; design: BuildingDesign } => {
  const resolvedDesign =
    design ??
    applyMissingCalculatedAreaActivities(
      migrateProjectMetadataToBuildingDesign(meta),
    );
  const designWithBuilding = ensureDesignHasBuilding(resolvedDesign);
  const gfa = getDesignGfa(designWithBuilding);
  return {
    meta: { ...meta, design: { gfa } },
    design: designWithBuilding,
  };
};

const getDefaultDesignAndMeta = () => resolveDesignAndMeta(defaultMetadata);

const defaultProject: Readonly<
  Omit<Project, 'owner' | 'created_at' | 'updated_at'>
> = {
  id: 0,
  organizations: [],
  name: 'Project',
  baselines: [],
  ...getDefaultDesignAndMeta(),
  features: defaultBuildingFeatures,
  template: false,
  active_version_id: '',
  results: {},
};

/**
 * Create baselines (formerly building versions) from factory version inputs.
 * Always returns at least one baseline.
 */
const createBaselines = (
  versions: Partial<IFactoryVersion>[] | undefined,
  regenerateId: boolean,
): Baseline[] =>
  versions?.length
    ? versions.map((v) =>
        ensureVersionHasProposal(
          createElementOfType(ElementKind.Version, v, regenerateId),
        ),
      )
    : [
        ensureVersionHasProposal(
          createElementOfType(ElementKind.Version, undefined, regenerateId),
        ),
      ];

/**
 * @deprecated The building layer was flattened into {@link Project} (DEV-2576).
 * Builds the project-level building view (`meta`/`design`/`features`/`baselines`).
 */
export const createBuilding = (
  initialValues: IFactoryBuilding = {},
  regenerateId = true,
): IBuilding => {
  const baselines = createBaselines(initialValues.versions, regenerateId);
  const sourceMeta = initialValues.meta ?? defaultMetadata;
  const { meta, design } = resolveDesignAndMeta(
    sourceMeta,
    initialValues.design,
  );
  return {
    meta,
    design,
    features: initialValues.features ?? defaultBuildingFeatures,
    baselines,
  };
};

/**
 * Create a new project based. By default it will use the predifined defaultValues
 * but it's possible to override parts or the entire project.
 * Pass a mock tree structure to quickly create your own structure of products and elements
 * @param defaults A stripped down tree of elements used to create the entire structure
 * @param regenerateId If ids should be forced to regenerate
 * @returns
 */
export const createProject = (
  defaults: IFactoryProjectWithVersions,
  regenerateId = true,
): Project => {
  const owner = defaults.owner;
  if (!owner) {
    throw new Error('Project must have an owner');
  }

  // Support legacy building layer + version shortcut inputs (DEV-2576).
  const firstBuilding = defaults.buildings?.[0];
  const versionDefaults = defaults.versions?.length
    ? defaults.versions
    : (defaults.baselines ?? firstBuilding?.versions);

  const baselines = createBaselines(versionDefaults, regenerateId);

  const sourceMeta = defaults.meta ?? firstBuilding?.meta ?? defaultMetadata;
  const explicitDesign = defaults.design ?? firstBuilding?.design;
  const { meta, design } = resolveDesignAndMeta(sourceMeta, explicitDesign);
  const features =
    defaults.features ?? firstBuilding?.features ?? defaultBuildingFeatures;

  const newActiveVersionId = getNewActiveVersionId(
    defaults.active_version_id ?? '',
    versionDefaults ?? [],
    baselines,
  );

  return {
    ...defaultProject,
    ...getTimestamps(),
    marketEstimates: createMarketEstimatesSnapshot(),
    // We must omit factory-only / re-derived properties not belonging to Project interface
    ...omitUndefined(
      omit(
        defaults,
        'versions',
        'baselines',
        'buildings',
        'design',
        'meta',
        'features',
      ),
    ),
    baselines,
    design,
    meta,
    features,
    owner,
    active_version_id: newActiveVersionId,
  };
};

/**
 * Wraps a building version in a minimal {@link Project} for helpers that need project
 * context (e.g. {@link getExpressionVariablesRecord}) without a full persisted project.
 */
export const createProjectShellForVersion = (
  version: Baseline,
  options: { owner?: string } = {},
): Project => {
  const owner = options.owner ?? 'test-shell-owner';
  return createProject({ owner, versions: [version] }, false);
};

export const getNewActiveVersionId = (
  activeVersionId: Baseline['id'],
  versions: Baseline[] | Partial<IFactoryVersion>[],
  newVersions: Baseline[] | Partial<IFactoryVersion>[],
) => {
  const firstNewVersionId = newVersions[0]?.id;

  if (!firstNewVersionId) {
    console.warn('Could not find new versions');
    return '';
  }

  const activeVersionIndex = versions.findIndex(
    (v) => v.id === activeVersionId,
  );

  const newActiveVersionId =
    activeVersionIndex >= 0 ? newVersions[activeVersionIndex]?.id : undefined;

  return newActiveVersionId ?? firstNewVersionId;
};
